/**
 * Represents a collection of feature flags or properties used by PostHog.
 * Each flag is identified by a string key and can have a value of type boolean, string, number, or null.
 *
 * @example
 * const flags: PostHogFlags = {
 *   featureA: true,
 *   experimentGroup: "B",
 *   userScore: 42,
 *   deprecatedFlag: null
 * };
 */
type PostHogFlags = {
  [key: string]: boolean | string | number | null;
};

/**
 * Gets all the flags currently set in PostHog. And returns them as an object.
 */
/**
 * Retrieves all active PostHog feature flags and their current values.
 *
 * This function checks if the PostHog instance is available on the `window` object.
 * If available, it fetches all active feature flag keys and maps each key to its
 * corresponding value (either a variant or a boolean indicating if the flag is enabled).
 * The resulting flag values are logged to the console.
 *
 * @returns {PostHogFlags | undefined} An object containing the feature flag values,
 * or `undefined` if PostHog is not available.
 */
function getFlags(): PostHogFlags | undefined {
  try {
    console.debug("Retrieving PostHog flags");
    if (!window.posthog) return undefined;
    // Get all active feature flags
    const flagKeys = window?.posthog?.featureFlags?.getFlags();

    // Map each flag to its value (variant or true/false)
    const flagValues: any = {};
    flagKeys.forEach((key: string | number) => {
      flagValues[key] = window?.posthog?.getFeatureFlag(key);
    });

    // Optionally, log or send these values somewhere
    console.log("Feature flags at submit time:", flagValues);
    return flagValues;
  } catch (error) {
    console.error("Error retrieving PostHog flags:", error);
    return undefined;
  }
}

export const Posthog = {
  getFlags,
};